import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useNavigate } from "react-router-dom";

import UserProfile from '../../components/User';
import AccessController from '../../components/access_controller';

/**
 * Home is a launcher, used standing up in a kitchen, usually one-handed.
 *
 * It is laid out as a two-column grid of large tiles rather than a single
 * column of fixed-width buttons: the old layout used a 175px button on a 375px
 * screen, wasting half the width and pushing the last actions below the fold.
 *
 * Actions are grouped by the job they belong to, so the eye can skip to the
 * right group instead of reading eight similar labels. Colour marks the two
 * subtractive actions (usage, wastage) - it is not decoration.
 *
 * What appears depends on the account. Everyone gets the kitchen work and their
 * own leave and payslips; the business's performance and everybody else's pay
 * are owner-only, and are not shown at all rather than shown and refused.
 */
const GROUPS = [
    {
        title: 'Daily inventory',
        actions: [
            { label: 'Fill Inventory', path: '/inventory/add/status' },
            { label: 'Add Preparations', path: '/inventory/add/preparation' },
            { label: 'Add Usage', path: '/inventory/add/usage/', tone: 'warning' },
            { label: 'Add Wastage', path: '/inventory/add/wastage/', tone: 'error' },
        ],
    },
    {
        title: 'Bills',
        actions: [
            { label: 'Add Bill', path: '/bill/add/' },
            { label: 'View Bills', path: '/bill/view' },
        ],
    },
    {
        // Every action here is owner-only, so the whole group disappears for
        // everybody else rather than showing a heading over nothing.
        title: 'Orders and performance',
        owner: true,
        actions: [
            // Owner-only because the page is: /orders/view is
            // <AccessController require="owner"> and the endpoint behind it is
            // IsOwner, being order-level revenue for the whole business. Shown
            // to a cook it was a button that bounced them back to the home
            // screen - the "shown and refused" this file exists to avoid.
            { label: 'View Orders', path: '/orders/view' },
            { label: 'Performance', path: '/analytics' },
            { label: 'Fixed costs', path: '/analytics/fixed-costs' },
            { label: 'Menu', path: '/menu' },
            { label: 'Business settings', path: '/analytics/settings' },
        ],
    },
    {
        // Its own group rather than folded into "Daily inventory", which is the
        // kitchen's work, or into "Orders", which these are not. Both of these
        // are the owner checking that what was bought, what was cooked and what
        // is on the shelf agree with each other.
        title: 'Stock control',
        owner: true,
        actions: [
            { label: 'Stock reckoning', path: '/inventory/variance' },
            { label: 'Sort bill names', path: '/inventory/bill-names' },
            { label: 'View Inventory', path: '/inventory/view' },
        ],
    },
    {
        title: 'Me',
        actions: [
            { label: 'My leave & pay', path: '/me' },
            // No `owner: true` - this is a job for the kitchen, and hiding it
            // from them would leave nobody able to do it.
            { label: 'Recipes', path: '/recipes' },
        ],
    },
    {
        title: 'People',
        owner: true,
        actions: [
            { label: 'Staff', path: '/staff' },
            { label: 'Payroll', path: '/staff/payroll' },
            { label: 'Approvals', path: '/staff/requests' },
        ],
    },
];

function ActionTile(props) {
    const { label, tone, onClick } = props;
    return (
        <ButtonBase
            onClick={onClick}
            aria-label={label}
            sx={{
                width: '100%', borderRadius: 2, textAlign: 'left',
                // Comfortably tappable without aiming.
                minHeight: 72,
            }}
        >
            <Paper
                variant="outlined"
                sx={{
                    width: '100%', height: '100%', minHeight: 72,
                    display: 'flex', alignItems: 'center',
                    px: 2, py: 1.5, borderRadius: 2,
                    borderColor: tone ? `${tone}.main` : 'divider',
                    borderLeftWidth: tone ? 4 : 1,
                    transition: 'background-color 120ms',
                    '&:hover': { backgroundColor: 'action.hover' },
                }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                    {label}
                </Typography>
            </Paper>
        </ButtonBase>
    );
}

export default function HomePage() {
    const navigate = useNavigate();
    const username = UserProfile.getName();
    const isOwner = UserProfile.isOwner();

    const groups = GROUPS
        .filter((group) => !group.owner || isOwner)
        .map((group) => {
            const actions = group.actions.filter((action) => !action.owner || isOwner);
            return { ...group, actions };
        })
        .filter((group) => group.actions.length > 0);

    return (
        <AccessController onlyAdmin={false}>
            <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Store Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Welcome {username}
                    </Typography>
                </Box>

                {groups.map((group) => (
                    <Box key={group.title} sx={{ mb: 3 }}>
                        <Typography
                            variant="overline"
                            sx={{ color: 'text.secondary', letterSpacing: '.08em' }}
                        >
                            {group.title}
                        </Typography>
                        <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
                            {group.actions.map((action) => (
                                // Two per row on a phone, four on a tablet and up.
                                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={action.label}>
                                    <ActionTile
                                        label={action.label}
                                        tone={action.tone}
                                        onClick={() => navigate(action.path)}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                ))}
            </Box>
        </AccessController>
    );
}
